import { useCategoricalBarChartBaseConfig } from './useCategoricalBarChartBaseConfig.js';
import { useColorRule } from '../../core/color-rule/useColorRule.js';
import { useColorRuleConfig } from '../../core/context/ColorRuleContext.js';
import {
  type ColorToken,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import type { CategoricalChartData } from '../contexts/CategoricalBarChartDataContext.js';
import type {
  CategoricalBarData,
  CategoryMetadata,
  WeakMapCategory,
  WeakMapDimension,
} from '../types/categorical-bar-chart-data.js';
import type { CategoricalBarChartData } from '../types/categorical-bar-chart.js';
import {
  handleMultipleDimensions,
  handleSingleDimension,
  type SeriesEntry,
} from '../utils/generate-categorical-bar-data-helpers.js';
import { isGrouped } from '../utils/layout.js';

/**
 * Builds series array for ColorRule evaluation.
 * Multi-dimensional data is expanded into separate entries per subcategory.
 */
function buildSeriesForColorRule(
  initialData: CategoricalBarChartData[],
): SeriesEntry[] {
  const series: SeriesEntry[] = [];

  initialData.forEach((categoryData) => {
    if (typeof categoryData.value === 'object' && categoryData.value !== null) {
      Object.entries(categoryData.value).forEach(
        ([dimensionKey, dimensionValue]) => {
          series.push({
            ...categoryData,
            value: { [dimensionKey]: dimensionValue },
            _originalData: categoryData,
            _dimensionKey: dimensionKey,
          });
        },
      );
    } else {
      series.push({
        ...categoryData,
        _originalData: categoryData,
      });
    }
  });

  return series;
}

function createCategoryMetadata(id: string, name: string): CategoryMetadata {
  return { id, name };
}

/**
 * Generate bar data and weakmaps to be filled into categorical bar chart provider
 * - generate single or multiple dimensions object from consumer with ids
 * - generate colors by dimensions
 *
 * @param initialData - Categorical data from component
 * @returns Categorical chart data
 */
export function useGenerateCategoricalBarData(
  initialData: CategoricalBarChartData[],
): CategoricalChartData {
  const {
    valueRepresentation,
    colorPalette: configuredColorPalette,
    colorPaletteMode,
    groupMode,
  } = useCategoricalBarChartBaseConfig();
  const isRelative = valueRepresentation === 'relative';
  const firstCategoryUnit = initialData[0]?.unit;

  const colorRuleConfig = useColorRuleConfig();
  const hasColorRules = colorRuleConfig.length > 0;

  const seriesForColorRule = hasColorRules
    ? buildSeriesForColorRule(initialData)
    : [];
  const colorRules = useColorRule(seriesForColorRule, 'categoricalBar');
  const uniqueDimensions = new Set<string>();
  const categoricalData: CategoricalBarData[] = [];
  const categoryMap: WeakMapCategory = new WeakMap();
  const dimensionMap: WeakMapDimension = new WeakMap();
  const colorByDimension: Map<string, ColorToken> = new Map();
  const colorByCategory: Map<string, { [id: string]: ColorToken }> = new Map();
  for (const categoryData of initialData) {
    const categoryId = crypto.randomUUID();
    const {
      unit,
      category: categoryName,
      value: categoryValue,
      color: categoryColor,
      ...rest
    } = categoryData;
    const newCategory: CategoricalBarData = {
      id: categoryId,
      name: categoryName,
      unit,
      dimensions: [],
      ...rest,
    };
    const isSingleDimension =
      unit !== firstCategoryUnit || typeof categoryValue !== 'object';
    const categoricalColorPalette =
      colorPaletteMode === 'multi-color' ||
      isGrouped(groupMode) ||
      !isSingleDimension;

    const colorPalette =
      isValidColorPalette(configuredColorPalette) ||
      isCustomColorPalette(configuredColorPalette)
        ? configuredColorPalette
        : undefined;

    if (isSingleDimension) {
      const colorPositionIndex = initialData.indexOf(categoryData);
      const paletteColorPosition = categoricalColorPalette
        ? colorPositionIndex
        : 0;
      handleSingleDimension({
        categoryData,
        rest,
        categoryName,
        categoryColor,
        colorByDimension,
        colorRules,
        colorPalette,
        colorPaletteMode,
        paletteColorPosition,
        uniqueDimensions,
        dimensionMap,
        newCategory,
        seriesForColorRule,
        hasColorRules,
      });
    } else {
      handleMultipleDimensions({
        categoryData,
        rest,
        categoryName,
        colorByDimension,
        colorByCategory,
        colorRules,
        colorPalette,
        categoricalColorPalette,
        uniqueDimensions,
        dimensionMap,
        newCategory,
        seriesForColorRule,
        hasColorRules,
      });
    }
    const categoryMedatada = createCategoryMetadata(categoryId, categoryName);
    categoryMap.set(newCategory, categoryMedatada);
    categoricalData.push(newCategory);
  }
  return {
    categoryMap,
    dimensionMap,
    categoricalData,
    uniqueDimensions: Array.from(uniqueDimensions),
    unit: isRelative ? 'percent' : firstCategoryUnit,
    valueRepresentation,
  };
}
